import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, orderBy, query, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Plus, Trash2, Edit, Save, X } from 'lucide-react';

const FarmerManagement = () => {
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [liveBirds, setLiveBirds] = useState('');
  const [birdDate, setBirdDate] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingPhone, setEditingPhone] = useState('');
  const [editingLiveBirds, setEditingLiveBirds] = useState('');
  const [editingBirdDate, setEditingBirdDate] = useState('');

  useEffect(() => {
    fetchFarmers();
  }, []);

  const fetchFarmers = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'farmers'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      setFarmers(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching farmers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFarmer = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    try {
      await addDoc(collection(db, 'farmers'), {
        name: name.trim(),
        phone: phone.trim(),
        liveBirds: parseFloat(liveBirds) || 0,
        birdDate: birdDate || null,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      setName('');
      setPhone('');
      setLiveBirds('');
      setBirdDate('');
      fetchFarmers();
    } catch (error) {
      console.error('Error adding farmer:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteFarmer = async (id) => {
    if (!window.confirm('Delete this farmer?')) return;
    try {
      await deleteDoc(doc(db, 'farmers', id));
      setFarmers(farmers.filter(f => f.id !== id));
    } catch (error) {
      console.error('Error deleting farmer:', error);
    }
  };

  const handleEditFarmer = (farmer) => {
    setEditingId(farmer.id);
    setEditingName(farmer.name || '');
    setEditingPhone(farmer.phone || '');
    setEditingLiveBirds(farmer.liveBirds?.toString() || '0');
    setEditingBirdDate(farmer.birdDate || '');
  };

  const handleSaveFarmer = async (farmerId) => {
    try {
      const newLiveBirds = parseFloat(editingLiveBirds) || 0;
      
      await updateDoc(doc(db, 'farmers', farmerId), {
        name: editingName.trim(),
        phone: editingPhone.trim(),
        liveBirds: newLiveBirds,
        birdDate: editingBirdDate || null,
        updatedAt: new Date()
      });
      
      console.log(`Updated farmer ${farmerId} live birds to ${newLiveBirds}`);
      
      setEditingId(null);
      setEditingName('');
      setEditingPhone('');
      setEditingLiveBirds('');
      setEditingBirdDate('');
      fetchFarmers();
    } catch (error) {
      console.error('Error updating farmer:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
    setEditingPhone('');
    setEditingLiveBirds('');
    setEditingBirdDate('');
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 max-w-6xl mx-auto">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Farmers</h2>
        <p className="text-sm text-gray-600 mt-1">
          Click "Edit" to modify any farmer's information. All fields can be edited including name, phone, live birds, and bird date.
        </p>
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            <strong>Live Birds Management:</strong> When you add mortality in farm records, the farmer's live birds count will automatically decrease. 
            To add new birds, simply edit the farmer and update the live birds count to the new total.
          </p>
        </div>
      </div>
      <form onSubmit={handleAddFarmer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-6">
        <input
          type="text"
          placeholder="Farmer Name"
          value={name}
          onChange={e => setName(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
        <input
          type="text"
          placeholder="Phone (optional)"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="number"
          placeholder="Live Birds"
          value={liveBirds}
          onChange={e => setLiveBirds(e.target.value)}
          min="0"
          step="1"
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="date"
          placeholder="Bird Date"
          value={birdDate}
          onChange={e => setBirdDate(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={adding}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          <Plus size={16} /> Add Farmer
        </button>
      </form>
      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : farmers.length === 0 ? (
        <div className="text-gray-500 text-center py-8">No farmers found.</div>
      ) : (
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Live Birds</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bird Date</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {farmers.map(farmer => (
              <tr key={farmer.id} className={editingId === farmer.id ? 'bg-yellow-50' : ''}>
                <td className="px-4 py-2">
                  {editingId === farmer.id ? (
                    <input
                      type="text"
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  ) : (
                    <span className="text-gray-900">{farmer.name}</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {editingId === farmer.id ? (
                    <input
                      type="text"
                      value={editingPhone}
                      onChange={e => setEditingPhone(e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <span className="text-gray-900">{farmer.phone || '-'}</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {editingId === farmer.id ? (
                    <input
                      type="number"
                      value={editingLiveBirds}
                      onChange={e => setEditingLiveBirds(e.target.value)}
                      min="0"
                      step="1"
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <span className="text-gray-900 font-medium">{farmer.liveBirds || 0}</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {editingId === farmer.id ? (
                    <input
                      type="date"
                      value={editingBirdDate}
                      onChange={e => setEditingBirdDate(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <span className="text-gray-900">
                      {farmer.birdDate ? new Date(farmer.birdDate).toLocaleDateString() : '-'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {editingId === farmer.id ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSaveFarmer(farmer.id)}
                        className="text-green-600 hover:text-green-900 flex items-center gap-1"
                        title="Save Changes"
                      >
                        <Save size={14} /> Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
                        title="Cancel"
                      >
                        <X size={14} /> Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditFarmer(farmer)}
                        className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                        title="Edit Farmer"
                      >
                        <Edit size={14} /> Edit
                      </button>
                      <button
                        onClick={() => handleDeleteFarmer(farmer.id)}
                        className="text-red-600 hover:text-red-900 flex items-center gap-1"
                        title="Delete"
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default FarmerManagement; 